import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Printer, Download, Loader2, Eye } from "lucide-react";
import { api } from "@/lib/api";
import { downloadFile } from "@/lib/download";
import { Button } from "@/components/ui-kit/Button";
import { LedgerStatement } from "@/components/ledger/LedgerStatement";
import { PdfPreviewDialog } from "@/components/pdf/PdfPreviewDialog";

/** Standalone, focusable, printable per-matter reconciliation statement. */
export function MatterStatementPage() {
  const { matterId } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [dl, setDl] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    api.get(`/matters/${matterId}/statement`).then((r) => setMeta(r.data)).catch(() => {});
  }, [matterId]);

  const downloadPdf = async () => {
    setDl(true);
    try { await downloadFile(`/matters/${matterId}/statement/pdf`); } finally { setDl(false); }
  };

  return (
    <div className="space-y-6" data-testid="matter-statement-page">
      <div className="flex flex-wrap items-start justify-between gap-4 print:hidden">
        <button onClick={() => navigate(`/workbench/${matterId}`)} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="statement-back">
          <ArrowLeft className="h-4 w-4" /> Back to matter
        </button>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => window.print()} data-testid="statement-print">
            <Printer className="h-4 w-4" /> Print
          </Button>
          <Button variant="secondary" onClick={() => setPreviewOpen(true)} data-testid="preview-statement-pdf">
            <Eye className="h-4 w-4" /> Preview
          </Button>
          <Button onClick={downloadPdf} disabled={dl} data-testid="statement-download-pdf">
            {dl ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download PDF
          </Button>
        </div>
      </div>

      {!meta ? (
        <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : (
        <div>
          <h1 className="font-serif text-3xl font-semibold tracking-tight text-foreground">Reconciliation statement</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            {meta.client_name ? (
              <Link to={`/clients-matters/${meta.client_id}`} className="text-foreground hover:underline">{meta.client_name}</Link>
            ) : "—"}{" · "}{meta.matter_name}
          </p>
        </div>
      )}

      <LedgerStatement matterId={matterId} />

      <PdfPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        path={`/matters/${matterId}/statement/pdf`}
        title="Statement preview"
      />
    </div>
  );
}

export default MatterStatementPage;
